<template>
  <div class="max-w-6xl mx-auto space-y-6">
    <Head title="Nuevo Traslado de Inventario" />

    <!-- Top Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <div class="flex items-center gap-3">
          <Link
            :href="`/deposits/${selectedEmitterId}/transfer`"
            class="w-9 h-9 rounded-xl bg-white border border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50 flex items-center justify-center transition-colors shadow-2xs"
            title="Volver"
          >
            <i class="fa fa-arrow-left text-sm" />
          </Link>
          <div>
            <h1 class="text-2xl font-extrabold text-gray-900 tracking-tight">
              Registrar Traslado de Inventario
            </h1>
            <p class="text-xs text-gray-500">
              Mudar mercancía de <strong>{{ selectedEmitterName }}</strong> hacia otro depósito o sede.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Form Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Left Column (2 cols): Products Search & Selected Items -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Transfer Parameters Box -->
        <div class="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm space-y-4">
          <h2 class="text-xs font-black uppercase tracking-wider text-gray-700 flex items-center gap-2">
            <i class="fa fa-route text-orange-600" />
            Parámetros del Traslado
          </h2>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Depósito Origen (Emisor) *</label>
              <select
                v-model="selectedEmitterId"
                class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-800 focus:bg-white focus:ring-orange-500 focus:border-orange-500 transition-colors"
                @change="onChangeEmitter"
              >
                <option v-for="dep in availableEmitterDeposits" :key="dep.id" :value="dep.id">
                  {{ dep.name }} (Origen)
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Depósito Destino (Receptor) *</label>
              <select
                v-model="form.deposit_receiver_id"
                required
                class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:bg-white focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="" disabled>Selecciona depósito destino...</option>
                <option v-for="dep in availableReceiverDeposits" :key="dep.id" :value="dep.id">
                  {{ dep.name }} (Destino)
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Fecha del Traslado *</label>
              <input
                v-model="form.date"
                type="date"
                required
                class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:bg-white focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Observaciones / Evento (Opcional)</label>
              <input
                v-model="form.notes"
                type="text"
                placeholder="Ej. Mercancía para evento Expo, rotación de stock..."
                class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>
        </div>

        <!-- Available Products Picker -->
        <div class="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm space-y-3">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h2 class="text-xs font-black uppercase tracking-wider text-gray-700 flex items-center gap-2">
              <i class="fa fa-boxes text-orange-600" />
              Productos Disponibles en {{ selectedEmitterName }}
            </h2>
            <div class="relative w-full sm:w-64">
              <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                v-model="searchFilter"
                type="text"
                placeholder="Filtrar por nombre o código..."
                class="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:bg-white focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          <!-- Product Quick List -->
          <div class="max-h-60 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-2xl">
            <div
              v-for="product in filteredAvailableProducts"
              :key="product.id"
              class="p-2.5 flex items-center justify-between gap-3 hover:bg-orange-50/40 cursor-pointer transition-colors"
              @click="addProductToTransfer(product)"
            >
              <div class="min-w-0">
                <div class="text-xs font-bold text-gray-900 truncate">{{ product.name }}</div>
                <div class="text-[11px] font-mono text-gray-400">Código: {{ product.code }}</div>
              </div>
              <div class="flex items-center gap-3 shrink-0">
                <span
                  class="px-2 py-0.5 rounded-full text-[11px] font-bold"
                  :class="product.stock > 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'"
                >
                  Stock: {{ product.stock }}
                </span>
                <button
                  type="button"
                  class="w-6 h-6 rounded-lg bg-orange-100 hover:bg-orange-600 text-orange-700 hover:text-white flex items-center justify-center transition-colors text-xs font-bold"
                  @click.stop="addProductToTransfer(product)"
                >
                  <i class="fa fa-plus" />
                </button>
              </div>
            </div>
            <div v-if="filteredAvailableProducts.length === 0" class="p-6 text-center text-xs text-gray-400">
              No se encontraron productos disponibles con ese término.
            </div>
          </div>
        </div>

        <!-- Selected Products for Transfer -->
        <div class="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm space-y-3">
          <h2 class="text-xs font-black uppercase tracking-wider text-gray-700 flex items-center gap-2">
            <i class="fa fa-clipboard-check text-orange-600" />
            Productos Seleccionados para Trasladar ({{ form.products.length }})
          </h2>

          <div v-if="form.products.length === 0" class="py-10 text-center border-2 border-dashed border-gray-200 rounded-2xl text-gray-400">
            <i class="fa fa-dolly text-3xl mb-2 text-gray-300" />
            <p class="text-xs font-medium">Haz clic en los productos de arriba para agregarlos al traslado.</p>
          </div>

          <div v-else class="overflow-x-auto">
            <table class="w-full text-left border-collapse text-xs">
              <thead>
                <tr class="bg-gray-50 border-b border-gray-200 text-gray-500 font-bold uppercase text-[10px]">
                  <th class="py-2.5 px-3">Producto</th>
                  <th class="py-2.5 px-3 text-center">Stock Origen</th>
                  <th class="py-2.5 px-3 text-center" style="width: 140px;">Cantidad a Trasladar</th>
                  <th class="py-2.5 px-3 text-right">Quitar</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="(item, index) in form.products" :key="item.id" class="hover:bg-gray-50/50">
                  <td class="py-2.5 px-3">
                    <div class="font-bold text-gray-900">{{ item.name }}</div>
                    <div class="font-mono text-[10px] text-gray-400">{{ item.code }}</div>
                  </td>
                  <td class="py-2.5 px-3 text-center font-bold text-gray-600">
                    {{ item.stock }}
                  </td>
                  <td class="py-2.5 px-3 text-center">
                    <div class="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        class="w-6 h-6 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs"
                        @click="decrementQty(index)"
                      >
                        -
                      </button>
                      <input
                        v-model.number="item.quantity"
                        type="number"
                        min="1"
                        :max="item.stock > 0 ? item.stock : 99999"
                        class="w-16 px-2 py-1 text-center font-bold bg-white border border-gray-200 rounded-lg text-xs focus:ring-orange-500 focus:border-orange-500"
                      />
                      <button
                        type="button"
                        class="w-6 h-6 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs"
                        @click="incrementQty(index)"
                      >
                        +
                      </button>
                    </div>
                  </td>
                  <td class="py-2.5 px-3 text-right">
                    <button
                      type="button"
                      class="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar de la lista"
                      @click="removeProduct(index)"
                    >
                      <i class="fa fa-trash-alt" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Right Column (1 col): Summary & Confirm Card -->
      <div class="space-y-4">
        <div class="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm space-y-4 sticky top-6">
          <h2 class="text-sm font-black text-gray-900 uppercase tracking-wider border-b border-gray-100 pb-3 flex items-center gap-2">
            <i class="fa fa-file-invoice text-orange-600" />
            Resumen del Traslado
          </h2>

          <div class="space-y-2.5 text-xs">
            <div class="flex justify-between text-gray-600">
              <span>Depósito Origen:</span>
              <strong class="text-gray-900">{{ selectedEmitterName }}</strong>
            </div>

            <div class="flex justify-between text-gray-600">
              <span>Depósito Destino:</span>
              <strong class="text-orange-600">{{ selectedReceiverName || 'No seleccionado' }}</strong>
            </div>

            <div class="flex justify-between text-gray-600">
              <span>Fecha Programada:</span>
              <strong class="text-gray-900">{{ form.date }}</strong>
            </div>

            <div class="border-t border-gray-100 pt-2.5 flex justify-between text-gray-600">
              <span>Productos Distintos:</span>
              <strong class="text-gray-900">{{ form.products.length }}</strong>
            </div>

            <div class="flex justify-between text-sm font-black text-gray-900 border-t border-gray-200 pt-2.5">
              <span>Total Unidades:</span>
              <span class="text-orange-600 text-base">{{ totalUnits }} unds</span>
            </div>
          </div>

          <div class="p-3 bg-blue-50 rounded-2xl border border-blue-200 text-[11px] text-blue-900 space-y-1">
            <div class="font-bold flex items-center gap-1.5">
              <i class="fa fa-info-circle text-blue-600" />
              Movimiento en Tiempo Real
            </div>
            <p>
              Al confirmar, el stock se descontará de <strong>{{ selectedEmitterName }}</strong> y se acreditará inmediatamente en el depósito destino. Podrás reingresarlo cuando termine el evento.
            </p>
          </div>

          <button
            type="button"
            :disabled="submitting || !canSubmit"
            class="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-2xl font-bold text-sm shadow-md transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
            @click="submitTransfer"
          >
            <i v-if="submitting" class="fa fa-spinner fa-spin" />
            <i v-else class="fa fa-check-circle" />
            <span>{{ submitting ? 'Procesando Traslado...' : 'Confirmar y Trasladar' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Swal from 'sweetalert2'

export default {
  name: 'TransferCreate',

  components: {
    Head,
    Link,
  },

  layout: Layout,

  props: {
    depositId: [Number, String],
    deposit: Object,
    deposits: Array,
    allDeposits: Array,
    products: Array,
    filters: Object,
  },

  data() {
    const emitterId = Number(this.depositId || this.deposit?.id)
    return {
      submitting: false,
      searchFilter: '',
      selectedEmitterId: emitterId,
      form: {
        deposit_emitter_id: emitterId,
        deposit_receiver_id: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
        products: [],
      },
    }
  },

  computed: {
    availableEmitterDeposits() {
      if (this.allDeposits && this.allDeposits.length) {
        return this.allDeposits
      }
      const list = []
      if (this.deposit) list.push(this.deposit)
      if (this.deposits) {
        this.deposits.forEach(d => {
          if (!list.some(x => x.id === d.id)) list.push(d)
        })
      }
      return list
    },

    availableReceiverDeposits() {
      return this.availableEmitterDeposits.filter(d => Number(d.id) !== Number(this.selectedEmitterId))
    },

    selectedEmitterName() {
      const em = this.availableEmitterDeposits.find(d => Number(d.id) === Number(this.selectedEmitterId))
      return em ? em.name : (this.deposit?.name || '')
    },

    selectedReceiverName() {
      const rec = this.availableEmitterDeposits.find(d => Number(d.id) === Number(this.form.deposit_receiver_id))
      return rec ? rec.name : ''
    },

    filteredAvailableProducts() {
      if (!this.searchFilter) return this.products || []
      const term = this.searchFilter.toLowerCase()
      return (this.products || []).filter(
        p => (p.name && p.name.toLowerCase().includes(term)) || (p.code && p.code.toLowerCase().includes(term)),
      )
    },

    totalUnits() {
      return this.form.products.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)
    },

    canSubmit() {
      return (
        this.selectedEmitterId &&
        this.form.deposit_receiver_id &&
        Number(this.selectedEmitterId) !== Number(this.form.deposit_receiver_id) &&
        this.form.date &&
        this.form.products.length > 0 &&
        this.form.products.every(p => p.quantity > 0)
      )
    },
  },

  watch: {
    selectedEmitterId(newVal) {
      if (Number(this.form.deposit_receiver_id) === Number(newVal)) {
        const nextReceiver = this.availableReceiverDeposits.find(d => Number(d.id) !== Number(newVal))
        this.form.deposit_receiver_id = nextReceiver ? nextReceiver.id : ''
      }
    },
  },

  created() {
    const receivers = this.availableReceiverDeposits
    if (receivers.length > 0 && !this.form.deposit_receiver_id) {
      this.form.deposit_receiver_id = receivers[0].id
    }
  },

  methods: {
    onChangeEmitter() {
      const currentOrigin = Number(this.depositId || this.deposit?.id)
      if (Number(this.selectedEmitterId) === currentOrigin) return

      const doChange = () => {
        this.$inertia.visit(`/deposits/${this.selectedEmitterId}/transfer/create`)
      }

      if (this.form.products.length > 0) {
        Swal.fire({
          title: '¿Cambiar depósito origen?',
          text: 'Se limpiará la lista de productos seleccionados para cargar las existencias reales del nuevo depósito.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, cambiar',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#ea580c',
        }).then(result => {
          if (result.isConfirmed) {
            doChange()
          } else {
            this.selectedEmitterId = currentOrigin
          }
        })
      } else {
        doChange()
      }
    },

    addProductToTransfer(prod) {
      const existing = this.form.products.find(p => p.id === prod.id)
      if (existing) {
        if (existing.stock <= 0 || existing.quantity < existing.stock) {
          existing.quantity++
        }
      } else {
        this.form.products.push({
          id: prod.id,
          name: prod.name,
          code: prod.code,
          stock: prod.stock,
          quantity: 1,
        })
      }
    },

    incrementQty(index) {
      const item = this.form.products[index]
      if (item.stock <= 0 || item.quantity < item.stock) {
        item.quantity++
      }
    },

    decrementQty(index) {
      const item = this.form.products[index]
      if (item.quantity > 1) {
        item.quantity--
      }
    },

    removeProduct(index) {
      this.form.products.splice(index, 1)
    },

    submitTransfer() {
      if (!this.canSubmit) {
        Swal.fire({
          icon: 'warning',
          title: 'Datos incompletos',
          text: 'Verifica los depósitos seleccionados y asegúrate de agregar productos con cantidad válida.',
        })
        return
      }

      Swal.fire({
        title: '¿Confirmar traslado de mercancía?',
        html: `Se trasladarán <b>${this.totalUnits} unidades</b> de <b>${this.selectedEmitterName}</b> hacia <b>${this.selectedReceiverName}</b>.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, trasladar ahora',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ea580c',
      }).then(result => {
        if (result.isConfirmed) {
          this.submitting = true
          this.form.deposit_emitter_id = this.selectedEmitterId
          this.$inertia.post(`/deposits/${this.selectedEmitterId}/transfer`, this.form, {
            preserveScroll: true,
            onError: (errors) => {
              this.submitting = false
              const msg = errors.error || Object.values(errors)[0] || 'Ocurrió un error al procesar el traslado.'
              Swal.fire({ icon: 'error', title: 'Error', text: msg })
            },
            onSuccess: () => {
              this.submitting = false
            },
          })
        }
      })
    },
  },
}
</script>
